import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { AppearanceSettings } from '../types';

const STORAGE_KEY = 'wfx-appearance';

const DEFAULTS: AppearanceSettings = {
  theme: 'system',
  accent: 'orange',
  fontSize: 'medium',
  compactMode: false,
  animations: true,
};

function loadSettings(): AppearanceSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    /* ignore malformed storage */
  }
  return DEFAULTS;
}

function applySettings(settings: AppearanceSettings) {
  const root = document.documentElement;
  if (settings.theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', settings.theme);
  root.setAttribute('data-accent', settings.accent);
  root.setAttribute('data-font', settings.fontSize);
  root.classList.toggle('compact-mode', settings.compactMode);
  root.classList.toggle('no-animations', !settings.animations);
}

function applyLanguage(language?: string | null) {
  const root = document.documentElement;
  const value = String(language || '').trim().toLowerCase();
  if (value === 'hindi') root.lang = 'hi';
  else if (value === 'marathi') root.lang = 'mr';
  else if (value === 'nepali') root.lang = 'ne';
  else root.lang = 'en';
}

interface AppearanceContextValue {
  settings: AppearanceSettings;
  setSettings: (patch: Partial<AppearanceSettings>) => void;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export function AppearanceProvider({ children }: { children: React.ReactNode }) {
  const [settings, setState] = useState<AppearanceSettings>(loadSettings);

  useEffect(() => {
    applySettings(settings);
  }, [settings]);

  useEffect(() => {
    const load = () => {
      try {
        const raw = localStorage.getItem('wfx-profile');
        if (raw) {
          const parsed = JSON.parse(raw);
          applyLanguage(parsed?.preferredLanguage);
          return;
        }
      } catch {
        /* ignore malformed storage */
      }
      applyLanguage('English');
    };

    const onLanguage = (event: Event) => {
      const custom = event as CustomEvent<string | undefined>;
      applyLanguage(custom.detail);
    };

    load();
    window.addEventListener('storage', load);
    window.addEventListener('wfx:profile-language', onLanguage as EventListener);
    return () => {
      window.removeEventListener('storage', load);
      window.removeEventListener('wfx:profile-language', onLanguage as EventListener);
    };
  }, []);

  const setSettings = useCallback((patch: Partial<AppearanceSettings>) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore storage write failures */
      }
      return next;
    });
  }, []);

  return (
    <AppearanceContext.Provider value={{ settings, setSettings }}>
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) throw new Error('useAppearance must be used within an AppearanceProvider');
  return ctx;
}
